"use client"

import { ChevronDown, ListChecks, Settings2, Signal } from "lucide-react"
import { useEffect, useMemo } from "react"

import { trackYohoEvent } from "@/lib/analytics/track"

import {
  accountText,
  getSafeEsimTrackingId,
  pickValue,
  type AnyRecord,
  type LocaleProps,
} from "../personal-web"
import { trackInstall } from "./analytics"
import { BrandPills } from "./components/BrandPills"
import { DeviceTabs } from "./components/DeviceTabs"
import { Hero } from "./components/Hero"
import { ImportantTips } from "./components/ImportantTips"
import { InstallMethodToggle } from "./components/InstallMethodToggle"
import { ManualCard } from "./components/ManualCard"
import { NetworkInfoBox } from "./components/NetworkInfoBox"
import { QRCard } from "./components/QRCard"
import { SectionHeader } from "./components/SectionHeader"
import { StepList } from "./components/StepList"
import { TapToInstallButton } from "./components/TapToInstallButton"
import { getFlow } from "./data/steps"
import {
  supportsIosDeeplink,
  useDeviceDetect,
} from "./hooks/useDeviceDetect"
import { useEsimState } from "./hooks/useEsimState"
import { useInstallState } from "./hooks/useInstallState"
import {
  deviceKeyFor,
  splitDeviceKey,
  type AndroidBrand,
  type DeviceOs,
} from "./types"

/**
 * eSIM install + connect guide — 3-step layout.
 *
 * Layout (top to bottom):
 *   1. Hero (legacy) — promotes the Yoho app for one-tap install
 *   2. Transition copy — "or install manually below ↓"
 *   3. DeviceTabs — iOS / Android segmented control
 *   4. BrandPills — visible only when Android is selected
 *   5. Step 1 (Before you install) — SectionHeader + 4 ImportantTips cards
 *   6. Step 2 (Install your eSIM) — SectionHeader + Tap-to-install button
 *      (iOS 17.4+) + QR/Manual toggle + QRCard/ManualCard + StepList
 *   7. Step 3 (Connect) — SectionHeader + StepList + NetworkInfoBox
 *
 * UA-driven defaults are reconciled with the URL (?device=&method=) and
 * localStorage by useInstallState — see that hook for details.
 */
export function InstallationPanel({
  lng,
  esim,
}: LocaleProps & { esim: AnyRecord }) {
  const qrValue = pickValue(esim, [
    "qr_code",
    "qrcode",
    "qrCode",
    "activation_qr_code",
  ])
  // Strapi `esim.lpa` is the single source of truth. SM-DP+ + Activation
  // Code are derived from it on demand by ManualCard via lpa.ts#parseLpa.
  const lpa = pickValue(esim, ["lpa", "activation_code", "ac"])
  // Plan-specific APN. Strapi top-level `apn` is the canonical field, with
  // a fallback to variant_metadata.apn for older eSIM snapshots. iOS shows
  // it as a manual override hint ("if auto setup fails, enter X"); Android
  // shows it as the primary value because Android always needs an explicit
  // APN entry.
  const variantApn =
    pickValue(esim, ["apn"]) ||
    pickValue(
      (esim?.variant_metadata as AnyRecord | undefined) || {},
      ["apn"]
    )

  const qrSrc = useMemo(() => {
    if (qrValue && /^https?:\/\//i.test(qrValue)) return qrValue
    const payload = qrValue || lpa
    if (!payload) return ""
    // Public QR API — kept for the phase-1 demo; the v3 P0 plan replaces
    // this with a client-side generator (LPA shouldn't transit a 3rd party).
    return `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=12&data=${encodeURIComponent(payload)}`
  }, [qrValue, lpa])

  const detected = useDeviceDetect()
  const esimState = useEsimState(esim)

  // UA fills our defaults so iOS users land on the iOS tab without a click.
  // Default falls back to iOS — most Yoho eSIM customers are on iPhone, and
  // Android users notice the wrong tab faster than iOS users notice missing
  // hand-holding.
  const defaultDevice = useMemo(() => {
    if (detected.os === "android") {
      return deviceKeyFor("android", detected.brand ?? "generic")
    }
    return deviceKeyFor("ios")
  }, [detected.os, detected.brand])

  const { device, method, setDevice, setMethod } = useInstallState({
    device: defaultDevice,
    method: "qr",
  })

  // Re-emit detect → device whenever UA finishes resolving AFTER the user
  // has not yet clicked anything (i.e. they're still on the SSR default).
  // We only nudge once per session so the user retains control after their
  // first click.
  useEffect(() => {
    if (!detected.detected) return
    if (typeof window === "undefined") return
    const url = new URL(window.location.href)
    if (url.searchParams.has("device")) return // User-shared link wins.
    // Only auto-nudge if the user hasn't touched device yet — we detect this
    // by checking if the current device matches our compile-time default ios.
    if (device !== "ios") return
    if (detected.os === "android") {
      setDevice(deviceKeyFor("android", detected.brand ?? "generic"))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detected.detected, detected.os, detected.brand])

  // Keying the effect on the hash (a string) rather than the `esim` object
  // ref means parent re-renders that hand us a fresh object with the same
  // ID won't re-fire the analytics event.
  const esimIdHash = getSafeEsimTrackingId(esim)
  useEffect(() => {
    trackYohoEvent("esim_usage_view", {
      esim_id_hash: esimIdHash,
      usage_state: "installation_view",
    })
  }, [esimIdHash])

  const { os, brand } = splitDeviceKey(device)
  const flow = getFlow(device)
  const showDeeplink =
    os === "ios" && supportsIosDeeplink(detected) && Boolean(lpa)

  const handleOsChange = (next: DeviceOs) => {
    const nextDevice =
      next === "ios" ? deviceKeyFor("ios") : deviceKeyFor("android", brand ?? "generic")
    trackInstall({ name: "install_tab_changed", device: nextDevice, from: device })
    setDevice(nextDevice)
  }

  const handleBrandChange = (nextBrand: AndroidBrand) => {
    const nextDevice = deviceKeyFor("android", nextBrand)
    trackInstall({ name: "install_tab_changed", device: nextDevice, from: device })
    setDevice(nextDevice)
  }

  const handleMethodChange = (next: "qr" | "manual") => {
    trackInstall({
      name: "install_method_changed",
      device,
      method: next,
      // `from` may be "deeplink" when the user is leaving the iOS one-tap
      // path for QR/Manual — preserve that signal so the funnel can see
      // deeplink→qr / deeplink→manual transitions instead of dropping the
      // field entirely (GA strips undefined keys).
      from: method,
    })
    setMethod(next)
  }

  const installSteps =
    method === "manual" ? flow.install.manual : flow.install.qr

  return (
    <div className="grid gap-5 pb-6">
      <Hero lng={lng} />

      <p className="-mt-2 flex items-center justify-center gap-2 px-1 text-center text-[13px] font-medium text-stone-500">
        <span>{accountText(lng, "install.hero.orManual")}</span>
        <ChevronDown className="h-3.5 w-3.5" aria-hidden />
      </p>

      <DeviceTabs lng={lng} device={device} onChange={handleOsChange} />

      {os === "android" ? (
        <BrandPills
          lng={lng}
          // Brands not surfaced as pills (e.g. android-xiaomi from a deep
          // link) fall through to the "Other brand" state so the row doesn't
          // render with zero active selections.
          brand={
            brand === "samsung" || brand === "pixel" ? brand : "generic"
          }
          onChange={handleBrandChange}
        />
      ) : null}

      {/* ── Step 1: Before you install ───────────────────────────── */}
      <SectionHeader
        lng={lng}
        id="section-before"
        step={1}
        titleKey="install.section.beforeTitle"
        subtitleKey="install.section.beforeSubtitle"
        icon={ListChecks}
      />

      <ImportantTips lng={lng} />

      {/* ── Step 2: Install ───────────────────────────────────────── */}
      <SectionHeader
        lng={lng}
        id="section-install"
        step={2}
        titleKey="install.section.installTitle"
        subtitleKey="install.section.installSubtitle"
        icon={Settings2}
      />

      {showDeeplink ? (
        <TapToInstallButton
          lng={lng}
          lpa={lpa}
          onTapped={() =>
            trackInstall({
              name: "install_deeplink_tapped",
              device,
              ios_version: detected.iosVersion,
            })
          }
        />
      ) : null}

      <InstallMethodToggle
        lng={lng}
        method={method === "deeplink" ? "qr" : method}
        onChange={handleMethodChange}
      />

      {method === "manual" ? (
        <ManualCard
          lng={lng}
          lpa={lpa}
          onCopy={(field) =>
            trackInstall({
              name: "install_copy_clicked",
              device,
              method: "manual",
              field,
            })
          }
          onParseFailed={({ lpa_length, lpa_prefix_hash }) =>
            trackInstall({
              name: "install_lpa_parse_failed",
              lpa_length,
              lpa_prefix_hash,
            })
          }
        />
      ) : (
        <QRCard
          lng={lng}
          qrSrc={qrSrc}
          onQrSaved={() => trackInstall({ name: "install_qr_saved", device })}
        />
      )}

      <StepList
        lng={lng}
        steps={installSteps}
        ariaLabel={accountText(lng, "install.section.installAriaLabel")}
      />

      {/* ── Step 3: Connect ───────────────────────────────────────── */}
      <SectionHeader
        lng={lng}
        id="section-connect"
        step={3}
        titleKey="install.section.connectTitle"
        subtitleKey="install.section.connectSubtitle"
        icon={Signal}
      />

      <StepList
        lng={lng}
        steps={flow.connect}
        ariaLabel={accountText(lng, "install.section.connectAriaLabel")}
      />

      <NetworkInfoBox lng={lng} flow={flow} variantApn={variantApn} />

      {esimState.isTerminal ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900">
          {accountText(lng, "install.state.expired")}
        </div>
      ) : null}
    </div>
  )
}


